import router from '../router/router.js'
import axios from 'axios'

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        // A timeout or a dropped connection produces an error with no
        // `response` at all. Reading .status off it threw a TypeError that
        // replaced the real failure, so callers saw "cannot read properties of
        // undefined" instead of a timeout.
        if (error.response && error.response.status == 401) {
            localStorage.clear()
            router.push({ name: '/' })
            location.reload()
            throw error
        }
        throw error
    }
)

// Callers all branch on `response.data.response`. An HTTP error still carries
// that shape, but a timeout or dropped connection resolves with a bare Error
// that has no `.data`, so every call site threw a TypeError instead of handling
// the failure. Give those a response-shaped object so a timeout is just a
// normal falsy result.
function toResponse(err) {
    if (err && err.response) return err.response

    const isTimeout = err && (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || ''))
    return {
        status: 0,
        data: {
            response: false,
            message: isTimeout
                ? 'The server took too long to respond. Please try again.'
                : 'Could not reach the server. Please check your connection.',
        },
    }
}

export default function (Vue) {

    Vue.axios = {
        setupHeaderConfig(config, data) {
            let g_token;
            let token;
            if (JSON.parse(localStorage.getItem('vuex'))) {
                g_token = JSON.parse(localStorage.getItem('vuex'))
                token = g_token.auth.token
            }
            !config['headers'] ? config['headers'] = {} : null
            token ? config['headers']['Authorization'] = 'Bearer ' + token : null
            config['withCredentials'] = true
            config['baseURL'] = process.env.VUE_APP_API_URL

            const isFormData = typeof FormData !== 'undefined' && data instanceof FormData

            // A JSON Content-Type on a FormData body is not merely wrong, it is
            // destructive: axios sees it and serialises the FormData to JSON, and
            // every File becomes {}. Leave the header off and let the browser set
            // multipart/form-data with its boundary.
            if (isFormData) {
                delete config['headers']['Content-Type']
            } else if (!config['headers']['Content-Type']) {
                // Only set Content-Type to application/json if not already specified
                config['headers']['Content-Type'] = 'application/json'
            }

            // No Access-Control-* headers here on purpose. Those are RESPONSE
            // headers; sending them on the request achieved nothing and only
            // widened the set of headers each preflight had to approve. CORS
            // belongs server-side, in config/cors.php.
            //
            // Note this does not remove the preflight: Authorization and a JSON
            // Content-Type are both non-safelisted, so one is required either
            // way. What makes it cheap is caching it, via Access-Control-Max-Age
            // on the API.

            // Without a timeout a request that never gets a connection never
            // settles, so its await never returns and any `finally` that clears
            // a loading flag never runs — a starved page stays on its skeleton
            // forever instead of failing. Fail after 30s so callers can react.
            if (config['timeout'] === undefined) {
                config['timeout'] = 30000
            }

            return config
        },

        post(url, params, config = { headers: {} }) {
            return new Promise(function (resolve) {
                const newConfig = Vue.axios.setupHeaderConfig(config, params)
                axios.post(url, params, newConfig)
                    .then((response) => resolve(response))
                    .catch((err) => resolve(toResponse(err)))
            })
        },

        get(url, params, config = { headers: {} }) {
            return new Promise(function (resolve) {
                config['params'] = params
                const newConfig = Vue.axios.setupHeaderConfig(config)
                axios.get(url, newConfig)
                    .then((response) => resolve(response))
                    .catch((err) => resolve(toResponse(err)))
            })
        },

        put(url, params, config = { headers: {} }) {
            return new Promise(function (resolve) {
                const newConfig = Vue.axios.setupHeaderConfig(config, params)
                // console.log(newConfig)
                axios.put(url, params, newConfig)
                    .then((response) => resolve(response))
                    .catch((err) => resolve(toResponse(err)))
            })
        },

        patch(url, params, config = { headers: {} }) {
            return new Promise(function (resolve) {
                const newConfig = Vue.axios.setupHeaderConfig(config, params)
                axios.patch(url, params, newConfig)
                    .then((response) => resolve(response))
                    .catch((err) => resolve(toResponse(err)))
            })
        },

        delete(url, config = { headers: {} }) {
            return new Promise((resolve, reject) => {
                const newConfig = Vue.axios.setupHeaderConfig(config)
                axios.delete(url, {
                    ...newConfig
                })
                    .then((response) => resolve(response))
                    .catch((err) => reject(err.response || err))
            })
        }
    }

    Object.defineProperties(Vue.prototype, {
        $axios: {
            get: () => {
                return Vue.axios
            }
        }
    })
}